/**
 * Transport helpers that use sendBeacon when available and retry with backoff.
 */
import { sleep } from "./utils";

export type TransportOptions = {
  maxAttempts?: number;
  baseDelayMs?: number;
  useBeacon?: boolean;
};

export type TransportResult = {
  ok: boolean;
  status?: number;
};

export async function postJson(
  url: string,
  payload: unknown,
  options: TransportOptions = {},
): Promise<TransportResult> {
  const body = JSON.stringify(payload);
  const useBeacon =
    options.useBeacon &&
    typeof navigator !== "undefined" &&
    navigator.sendBeacon;

  if (useBeacon) {
    try {
      const ok = navigator.sendBeacon(
        url,
        new Blob([body], { type: "application/json" }),
      );
      if (ok) return { ok };
    } catch {
      // fall through
    }
  }

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body,
      keepalive: true,
      // Allow first-party cookie workflows (e.g. /pxl/firstparty) and
      // same-site tracking when the ingest domain is a CNAME under the site.
      credentials: "include",
    });
    return { ok: response.ok, status: response.status };
  } catch {
    return { ok: false };
  }
}

export async function postWithRetry(
  url: string,
  payload: unknown,
  options: TransportOptions = {},
): Promise<boolean> {
  const maxAttempts = options.maxAttempts ?? 3;
  let delay = options.baseDelayMs ?? 500;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const result = await postJson(url, payload, options);
    if (result.ok) return true;
    await sleep(delay);
    delay = Math.min(delay * 2, 5000);
  }
  return false;
}
